import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPopover } from './useAnchoredPopover';
import PickField, { type PickOption } from './PickField';
import Icon from './Icon';

/**
 * THE DOER-CAPTURE PROMPT — who is doing this, asked the moment work starts (A2).
 *
 * A card has two people now: the Accountable owner and the Responsible DOER (the
 * two-part assignee). The doer is the one that goes unset, because nobody is
 * asked for it at the point it becomes true — when the card starts moving. So the
 * board asks, once, exactly then: drag a card into In Progress with no doer and
 * this hangs off that card.
 *
 * ── WHY A POPOVER AND NOT A GATE ────────────────────────────────────────────
 *
 * It is a nudge, never a wall. "I'm on it" is the one primary action — a vendor
 * or anyone updating their own card names themselves in one click. "Someone
 * else…" opens the very same people picker the card's Responsible field uses, so
 * there is one way to name a doer, not two. And it is dismissable three ways —
 * "Not now", Escape, a click away — after which it is NEVER shown again for that
 * card and that mover (the caller remembers the pair). Asking twice would make
 * starting a card feel like being nagged, which is the opposite of the point.
 *
 * It reuses the field pickers' own machinery — `useAnchoredPopover` to pin to the
 * card and flip/shift to fit, a portal to <body> so no column clips it, and the
 * `.btn` family — so it reads as part of the board, not a bolted-on dialog.
 */
export function DoerPrompt({
  issueId,
  issueRef,
  options,
  suggest,
  onPickMe,
  onPick,
  onDismiss,
}: {
  /** The card this hangs off — found in the DOM by its `data-issue-id`. */
  issueId: string;
  /** Its human reference, e.g. "BCS-142", named in the prompt's sentence. */
  issueRef: string;
  /** The doer choices — the SAME list the Responsible field offers. */
  options: PickOption[];
  /** The likely doer to pre-highlight in "Someone else…" — the Accountable
      person (Vinnie, 23 Oct). A suggestion under the keyboard, not a commitment. */
  suggest?: string;
  /** The one-click primary — the mover takes the card as its doer. */
  onPickMe: () => void;
  /** Someone else was named (an email, an assistant id, or '' for Unassigned). */
  onPick: (who: string) => void;
  /** Closed without naming anyone — do not ask again for this card + mover. */
  onDismiss: () => void;
}) {
  const anchorRef = useRef<HTMLElement | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [picking, setPicking] = useState(false);

  // The anchor is the live card node in the In Progress column. Re-query every
  // render (the card re-renders as its status and doer change) BEFORE
  // useAnchoredPopover's own layout effect runs to place against it — hence a
  // layout effect declared first, so `anchorRef.current` is set in time.
  useLayoutEffect(() => {
    anchorRef.current =
      typeof document === 'undefined'
        ? null
        : document.querySelector<HTMLElement>(`[data-issue-id="${CSS.escape(issueId)}"]`);
  });

  const pos = useAnchoredPopover(true, anchorRef, popRef);

  // Escape and click-away both DISMISS — but only while the buttons are showing.
  // Once "Someone else…" has opened the nested picker, that picker owns Escape and
  // click-away (its option rows live in their own portal, outside ours), so our
  // listeners stand down to avoid dismissing the prompt out from under a pick.
  useEffect(() => {
    if (picking) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onDismiss();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (!popRef.current?.contains(e.target as Node)) onDismiss();
    };
    // Capture for Escape so a card-level handler cannot eat it first; click-away
    // on the next tick so the opening click does not immediately close it.
    document.addEventListener('keydown', onKey, true);
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
    };
  }, [picking, onDismiss]);

  const node = (
    <div
      ref={popRef}
      className="doer-pop"
      role="dialog"
      aria-label="Who is doing this work?"
      style={{
        position: 'fixed',
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : -9999,
        // Measured hidden, shown only once placed — a popover painted at -9999
        // first would otherwise flash in the corner (the field pickers' rule).
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <span className="doer-pop-caret" aria-hidden="true" />
      <div className="doer-pop-head">
        <Icon name="person" size={15} />
        <span className="doer-pop-title">Who&rsquo;s doing this work?</span>
      </div>
      {picking ? (
        <div className="doer-pop-pick">
          <PickField
            ariaLabel="Responsible"
            value=""
            suggest={suggest}
            options={options}
            autoOpen
            onChange={(v) => onPick(v)}
            /* Cancelling the picker (Escape, click-away, no choice) returns to the
               prompt rather than dismissing it — the person opened it on purpose. */
            onClose={(reason) => {
              if (reason !== 'pick') setPicking(false);
            }}
          />
        </div>
      ) : (
        <>
          <p className="doer-pop-body">
            You moved <b>{issueRef}</b> into In&nbsp;Progress. Name who&rsquo;s on it, or skip for now.
          </p>
          <div className="doer-pop-actions">
            <button type="button" className="btn btn-primary btn-small" onClick={onPickMe}>
              I&rsquo;m on it
            </button>
            <button type="button" className="btn btn-small" onClick={() => setPicking(true)}>
              Someone else&hellip;
            </button>
            <button type="button" className="doer-pop-skip" onClick={onDismiss}>
              Not now
            </button>
          </div>
        </>
      )}
    </div>
  );

  return typeof document === 'undefined' ? node : createPortal(node, document.body);
}
